import React, { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { addDiscountApi, getAllCustomerTypeApi, updateDiscountApi } from "@/api/settings/discount";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "@/hooks/useAuth";

// ----- Zod Schema -----
const discountSchema = z.object({
  order_type: z.string().min(1, "Required"),
  name: z.string().min(1, "Required"),

  // ⭐ Added final optimized days/time schema
  days: z.array(
    z.object({
      day: z.string(),
      enabled: z.boolean(),
      time: z.object({
        start: z.string().nullable(),
        end: z.string().nullable()
      })
    })
  ),

  discount_value: z.number().min(0),
  discount_type: z.string(),
  minimum_order: z.number().min(0),
  payment_method: z.string(),
  customer_type_id: z.string().nullable().optional(),

  menu_items: z
    .any()
    .transform((val) => (Array.isArray(val) ? val : [])), // fixed no-selection error

  not_conjunct_with_other: z.boolean().optional().default(false),
  status: z.boolean().optional().default(true)
});

// Weekday list
const weekDays = [
  "Saturday",
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday"
];

const menuItems = [
  { id: 1, name: "Drinks" },
  { id: 2, name: "Standard Foods" },
  { id: 3, name: "Dessert" },
  { id: 4, name: "Tea & Coffee" },
  { id: 5, name: "Pizza" },
  { id: 6, name: "Burger" },
  { id: 7, name: "Kebab" },
  { id: 8, name: "Fish & Chips" },
  { id: 9, name: "Combo Meal" },
  { id: 12, name: "Course" },
  { id: 13, name: "Special Menu" },
  { id: 14, name: "Dish of The Day" },
  { id: 15, name: "Sunday Roast" },
  { id: 16, name: "Banquet Night" },
  { id: 17, name: "Buffet" },
  { id: 18, name: "Christmas Lunch/Table" },
  { id: 19, name: "Party/Occasion platter sample" },
  { id: 20, name: "Mix & Match" },
  { id: 21, name: "Choose Your Own" },
  { id: 22, name: "Create Your Own Pizza" }
];


export default function EditDiscount() {
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    getValues,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(discountSchema),

    // ⭐ Default days with null times
    defaultValues: {
      days: weekDays.map((day) => ({
        day,
        enabled: false,
        time: { start: null, end: null }
      }))
    }
  });

  const [customerTypes, setCustomerTypes] = React.useState([]);
  const [discountId, setDiscountId] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();



  const { fields } = useFieldArray({
    control,
    name: "days"
  });

    function convertDays(data) {
        const dayMap = {
            "Monday": 1,
            "Tuesday": 2,
            "Wednesday": 3,
            "Thursday": 4,
            "Friday": 5,
            "Saturday": 6,
            "Sunday": 7
        };

        return data
            .filter(item => item.enabled)  // Only enabled days
            .map(item => ({
            day_id: dayMap[item.day],
            start_time: item.time.start,
            end_time: item.time.end
        }));
    }

    function convertDaysToForm(days = []) {
      // API returns items like { id: 6, name: 'Saturday', start_time: '09:00:00', end_time: '18:50:00' }
      // Form expects { day: 'Saturday', enabled: boolean, time: { start: '09:00', end: '18:50' } }

      const normalizeTime = (t) => {
        if (!t) return null;
        // strip seconds if provided (e.g. "18:50:00" -> "18:50")
        return t.length >= 5 ? t.slice(0, 5) : t;
      };

      const byId = {};
      const byName = {};
      days.forEach((d) => {
        if (d?.id != null) byId[d.id] = d;
        if (d?.name) byName[d.name.toLowerCase()] = d;
      });

      const nameToId = {
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
        Sunday: 7
      };

      return weekDays.map((day) => {
        const apiItem = byId[nameToId[day]] || byName[day.toLowerCase()];
        if (apiItem) {
          return {
            day,
            enabled: true,
            time: {
              start: normalizeTime(apiItem.start_time),
              end: normalizeTime(apiItem.end_time)
            }
          };
        }
        return {
          day,
          enabled: false,
          time: { start: null, end: null }
        };
      });
    }


  const onSubmit = (data) => {


    updateDiscountApi(discountId,{...data, days:convertDays(data.days), status: data?.status == true ?"active" :"inactive" }).then((data)=>{

      if(data?.status == "success"){
        toast.success("Discount update Successfully")
        reset()
        navigate("/backoffice/settings/discount")
      }else{
        toast.error("Something went wrong")
      }
    })
  };

  console.log("Errors:", errors);



  useEffect(()=>{
    getAllCustomerTypeApi( user?.restaurant_id, 0, "active").then((data)=>{

      if(data.status == "success"){
        setCustomerTypes(data?.data?.customer_types)
      }
    })
  }, [])


  useEffect(()=>{    
    if(location?.state){
        const discountData = location?.state;
      
        setDiscountId(discountData?.id)
        reset({
            order_type: discountData?.order_type,
            name: discountData?.name,
            discount_value: discountData?.discount_value,
            discount_type: discountData?.discount_type,
            minimum_order: discountData?.minimum_order,
            payment_method: discountData?.payment_method,
            customer_type_id: discountData?.customer_type?.id?.toString() || null,
            menu_items: discountData?.except_items?.map(item => item?.id?.toString()) || [],
            days: convertDaysToForm(discountData?.days),
            not_conjunct_with_other: discountData?.not_conjunct_with_other,
            status: discountData?.status === "active" ? true : false

        });
    }

    
  }, [location.state, reset]);



  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Overview" }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Settings", path: "" },
        { name: "Discount Management", path: "/backoffice/settings/discount" },
        { name: "Edit Discount", path: "/backoffice/settings/discount/edit" }
      ]}
    >
      <div className="min-h-screen text-white p-6 flex justify-center">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full max-w-4xl bg-color2 p-6 rounded-xl shadow-lg space-y-6"
        >
          <h2 className="text-2xl font-semibold text-center mb-4 pb-2 border-b border-color3">
            Edit Discount
          </h2>

          {/* Order Type */}
          <div>
            <label className="block mb-1 font-semibold">Order Type</label>
            <select className="w-full p-2 rounded bg-color3" {...register("order_type")}>
              <option value="">Select</option>
              <option value="pickup">Pickup</option>
              <option value="in_house">In House</option>
              <option value="delivery">Delivery</option>
            </select>
            {errors.order_type && (
              <p className="text-red-400 text-sm">{errors.order_type.message}</p>
            )}
          </div>

          {/* Discount Name */}
          <div>
            <label className="block mb-1 font-semibold">Discount Name</label>
            <input
              className="w-full p-2 rounded bg-color3"
              placeholder="Discount Name"
              {...register("name")}
            />
            {errors.name && <p className="text-red-400 text-sm">{errors.name.message}</p>}
          </div>

          {/* Discount Section */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-semibold">Discount</label>
              <input
                type="number"
                step="any"
                className="w-full p-2 rounded bg-color3"
                {...register("discount_value", { valueAsNumber: true })}
              />
            </div>

            <div>
              <label className="block mb-1 font-semibold">Discount Type</label>
              <select className="w-full p-2 rounded bg-color3" {...register("discount_type")}>
                <option value="percentage">Percentage</option>
                <option value="fixed_amount">Fixed Amount</option>
              </select>
            </div>
          </div>

          {/* Minimum Order */}
          <div>
            <label className="block mb-1 font-semibold">Minimum Order</label>
            <input
              type="number"
              className="w-full p-2 rounded bg-color3"
              {...register("minimum_order", { valueAsNumber: true })}
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block mb-1 font-semibold">Payment Method</label>
            <select className="w-full p-2 rounded bg-color3" {...register("payment_method")}>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="cash_or_card">Cash & Card</option>
            </select>
          </div>

          {/* ⭐ DAYS SECTION — Added Here */}
          <div className="bg-color3 p-3 rounded-lg">
            <label className="block mb-2 font-semibold text-lg">Available Days</label>

            <div className="space-y-3">
              {fields.map((item, index) => {
                const enabled = watch(`days.${index}.enabled`);

                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-3 gap-4 items-center p-3 rounded-lg"
                  >
                    {/* Checkbox */}
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" {...register(`days.${index}.enabled`)} className=" w-4 h-4 accent-color6 text-color5" />
                      {item.day}
                    </label>

                    {/* Start Time */}
                    <input
                      type="time"
                      disabled={!enabled}
                      className="bg-color3 p-1 rounded border border-color4"
                      {...register(`days.${index}.time.start`)}
                    />

                    {/* End Time */}
                    <input
                      type="time"
                      disabled={!enabled}
                      className="bg-color3 p-1 rounded border border-color4"
                      {...register(`days.${index}.time.end`)}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Customer Types */}
          <div className="bg-color3 p-2 rounded">
            <label className="block mb-2 font-semibold">Type of Customer</label>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <label key={null} className="flex items-center gap-2 cursor-pointer">
                <input
                    type="radio"
                    value={""}
                    className="w-5 h-5 accent-color6"
                    {...register("customer_type_id")}
                />
                None
              </label>
              {
                customerTypes.map((type, index) => (
                    <label key={type?.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="radio"
                        value={type?.id}
                        className="w-5 h-5 accent-color6"
                        {...register("customer_type_id")}
                    />
                    {type?.name}
                    </label>
                ))
              }
            </div>
          </div>

          {/* Except Items */}
          <div className="bg-color3 p-2 rounded">
            <label className="block mb-2 font-semibold">Except Items</label>
            <div className="grid grid-cols-3 gap-4 text-sm">
              {
                menuItems.map((item) => (
                    <label key={item.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        value={item.id}
                        className="w-4 h-4 accent-color6"
                        {...register("menu_items")}
                    />
                    {item.name}
                    </label>
                ))
              }
            </div>
          </div>

          {/* Not Conjunct */}
          <label className="flex items-center gap-2 font-semibold">
            <input type="checkbox" className="w-4 h-4 accent-color6" {...register("not_conjunct_with_other")} />
            Not applying with any other special discount
          </label>

          {/* Status */}
          <label className="flex items-center gap-2 font-semibold">
            <input type="checkbox" className="w-4 h-4 accent-color6"  {...register("status")} />
            Is Active
          </label>

          {/* Buttons */}
          <div className="flex justify-between gap-4 mt-6">
            <button type="button" onClick={()=> navigate("/backoffice/settings/discount")} className="w-full px-6 py-2 bg-red-600 rounded-lg">
              Cancel
            </button>
            <button type="submit" className="w-full px-6 py-2 bg-yellow-500 text-black rounded-lg">
              Confirm
            </button>
          </div>
        </form>
      </div>
    </Layout2>
  );
}
